import Breadcrumbs from '@storeblocks/breadcrumbs';
import { Button } from '@storeblocks/button';
import { Card, CardActions, CardHeadingGroup } from '@storeblocks/card';
import { type FC } from 'react';
import { Link, useParams } from 'react-router';
import styled from 'styled-components';

import { AnnouncementWarning } from '@/components/AnnouncementWarning';
import { CenteredPageContent } from '@/components/CenteredPageContent';
import { CardColumn, Row } from '@/components/Grid';
import { InternalLink } from '@/components/InternalLink';
import { MainContent } from '@/components/MainContent';
import { PageHeading } from '@/components/PageHeading';
import { useTexts } from '@/hooks/useTexts';
import { useGetTrackId } from '@/tracking/hooks/useGetTrackId';
import { TrackingElement } from '@/tracking/types/TrackingElement';

export const ContactUsPage: FC = () => {
  const texts = useTexts();
  const getTrackId = useGetTrackId();

  const { organizationCmId } = useParams();

  const rootPath = organizationCmId ? `/${organizationCmId}` : '/';

  return (
    <CenteredPageContent data-test="contact-us-page">
      <AnnouncementWarning />

      <Breadcrumbs expandAllAriaLabel={texts.breadcrumbs.expandAllAriaLabel}>
        <InternalLink to={rootPath}>{texts.menu.links.home}</InternalLink>
        <InternalLink to={`${rootPath}/contact-us`}>
          {texts.menu.links.contactUs}
        </InternalLink>
      </Breadcrumbs>

      <MainContent>
        <PageHeading header={texts.menu.links.contactUs} />
        <Row>
          <StyledCardColumn $span={6}>
            <Card>
              <CardHeadingGroup
                heading={texts.contactUs.generalInquiries.title}
                description={texts.contactUs.generalInquiries.description}
              />
              <CardActions>
                <Button
                  variant="secondary"
                  iconRight="envelope"
                  as={Link}
                  to={`mailto:${texts.contactUs.generalInquiries.mail}`}
                  data-trackid={getTrackId(
                    'send-mail-general-inquiries',
                    TrackingElement.Link,
                  )}
                >
                  {texts.contactUs.generalInquiries.buttonText}
                </Button>
              </CardActions>
            </Card>
          </StyledCardColumn>
          <StyledCardColumn $span={6}>
            <Card>
              <CardHeadingGroup
                heading={texts.contactUs.pensionServices.title}
                description={texts.contactUs.pensionServices.description}
              />
              <CardActions>
                <Button
                  variant="secondary"
                  iconRight="envelope"
                  as={Link}
                  to={`mailto:${texts.contactUs.pensionServices.mail}`}
                  data-trackid={getTrackId(
                    'send-mail-pension-services',
                    TrackingElement.Link,
                  )}
                >
                  {texts.contactUs.pensionServices.buttonText}
                </Button>
              </CardActions>
            </Card>
          </StyledCardColumn>
        </Row>
        <Row>
          <StyledCardColumn $span={6}>
            <Card>
              <CardHeadingGroup
                heading={texts.contactUs.clientExecutives.title}
                description={texts.contactUs.clientExecutives.description}
              />
              <CardActions>
                <Button
                  variant="secondary"
                  iconRight="external_link"
                  as={Link}
                  to={texts.contactUs.clientExecutives.link}
                  data-trackid={getTrackId(
                    'go-to-our-client-executives',
                    TrackingElement.Link,
                  )}
                >
                  {texts.contactUs.clientExecutives.buttonText}
                </Button>
              </CardActions>
            </Card>
          </StyledCardColumn>
        </Row>
      </MainContent>
    </CenteredPageContent>
  );
};

const StyledCardColumn = styled(CardColumn)`
  @media (max-width: 825px) {
    grid-column: span 12;
  }
`;
